/**
 * Vorlage „Ausstechen“.
 *
 * Schräg von oben: Der ausgerollte Teig liegt auf der Arbeitsfläche. Ein
 * Ausstecher senkt sich, drückt, hebt das Plätzchen heraus und setzt es auf
 * das Blech. Im Teig bleibt die Form als Loch zurück.
 */
import { ease, lerp, segment } from '../geometry.ts';
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { ellipsePath, polyline, smooth } from '../paths.ts';
import { compact, faded, group, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { arrow, counter, GROUND_Y } from '../parts/stage.ts';
import { choice, defineTemplate, textValue } from '../template.ts';

/** Schräge Draufsicht: Tiefe wird auf diesen Anteil gestaucht. */
const SQUASH = 0.45;
/** Teigplatte (Oberseite) und ihre Dicke. */
const SHEET = { left: 8, right: 164, top: 94, bottom: GROUND_Y - 4, slant: 12 } as const;
const SHEET_THICKNESS = 4;
/** Stelle, an der ausgestochen wird, und Platz auf dem Blech. */
const CUT = { x: 88, y: 116 } as const;
const TRAY = { left: 174, right: 236, y: GROUND_Y - 5 } as const;
const PLACE = { x: 205, y: TRAY.y - 11 } as const;
/** Halbe Breite der Form und Höhe des Ausstechers. */
const SIZE = 22;
const CUTTER_HEIGHT = 12;
/** Höhe über dem Teig, in der der Ausstecher schwebt. */
const HOVER = 52;

/** Zeitplan. */
const TIME = {
  appear: [0.02, 0.08],
  down: [0.1, 0.2],
  press: [0.2, 0.28],
  up: [0.3, 0.4],
  carry: [0.4, 0.54],
  set: [0.54, 0.62],
  leave: [0.64, 0.74],
  reset: [0.88, 1],
} as const;

/** Formen. */
const SHAPES = { star: 'Stern', heart: 'Herz', round: 'rund' } as const;
type Form = keyof typeof SHAPES;

/** Umriss der Form um (cx, cy), in der Tiefe gestaucht. */
function outline(form: Form, cx: number, cy: number): string {
  const project = (points: readonly Point[]): Point[] =>
    points.map((point) => ({ x: cx + point.x, y: cy + point.y * SQUASH }));
  switch (form) {
    case 'star': {
      const points = Array.from({ length: 10 }, (_, index) => {
        const angle = (index / 10) * Math.PI * 2 - Math.PI / 2;
        const r = index % 2 === 0 ? SIZE : SIZE * 0.46;
        return { x: Math.cos(angle) * r, y: Math.sin(angle) * r };
      });
      return polyline(project(points), true);
    }
    case 'heart': {
      const points = Array.from({ length: 24 }, (_, index) => {
        const a = (index / 24) * Math.PI * 2;
        // Herzkurve, auf SIZE skaliert, Spitze unten.
        const x = 16 * Math.sin(a) ** 3;
        const y = -(13 * Math.cos(a) - 5 * Math.cos(2 * a) - 2 * Math.cos(3 * a) - Math.cos(4 * a));
        return { x: (x / 16) * SIZE, y: (y / 16) * SIZE + 2 };
      });
      return smooth(project(points), true, 0.8);
    }
    default:
      return ellipsePath(cx, cy, SIZE, SIZE * SQUASH);
  }
}

/** Plätzchen: Oberseite mit sichtbarer Kante. */
function cookie(form: Form, at: Point): SceneNode[] {
  return [
    shape(outline(form, at.x, at.y + 3), styles.solid('shade')),
    shape(outline(form, at.x, at.y), styles.solid('paper')),
  ];
}

/** Ausstecher: Unterkante bei at, Wand nach oben. */
function cutter(form: Form, at: Point): SceneNode[] {
  return [
    line(at.x - SIZE * 0.98, at.y, at.x - SIZE * 0.98, at.y - CUTTER_HEIGHT, styles.detail()),
    line(at.x + SIZE * 0.98, at.y, at.x + SIZE * 0.98, at.y - CUTTER_HEIGHT, styles.detail()),
    shape(outline(form, at.x, at.y - CUTTER_HEIGHT), {
      fill: 'none',
      stroke: 'deep',
      strokeWidth: STROKE.detail,
    }),
    shape(outline(form, at.x, at.y), { fill: 'none', stroke: 'ink', strokeWidth: STROKE.outline }),
  ];
}

/** Vorlage „Ausstechen“. */
export const bakeCutOutTemplate = defineTemplate({
  id: 'bake.cut-out',
  group: 'bake',
  title: 'Ausstechen',
  description:
    'Aus dem ausgerollten Teig werden mit dem Ausstecher Plätzchen ausgestochen und mit Abstand auf das Blech gesetzt.',
  durationSeconds: 6,
  keywords: [
    'ausstechen',
    'ausgestochen',
    'ausstecher',
    'ausstechform',
    'plätzchen',
    'kekse',
    'sterne ausstechen',
    'aufs blech setzen',
  ],
  stillTime: 0.36,
  parameters: [choice('form', 'Form', SHAPES, 'star')],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (/stern/.test(lower)) return { form: 'star' };
    if (/herz/.test(lower)) return { form: 'heart' };
    if (/rund|kreis|glas/.test(lower)) return { form: 'round' };
    return {};
  },
  scene(values, t) {
    const form = textValue(values, 'form', 'star') as Form;
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const appear = ease.outCubic(segment(t, ...TIME.appear));
    const down = ease.inOutCubic(segment(t, ...TIME.down));
    const pressPhase = segment(t, ...TIME.press);
    const press = Math.sin(pressPhase * Math.PI) * 2;
    const up = ease.inOutCubic(segment(t, ...TIME.up));
    const carry = ease.inOutCubic(segment(t, ...TIME.carry));
    const set = ease.inOutCubic(segment(t, ...TIME.set));
    const leave = ease.inCubic(segment(t, ...TIME.leave));

    // Lage der Unterkante des Ausstechers.
    let at: Point;
    if (t < TIME.up[0]) {
      at = { x: CUT.x, y: CUT.y - HOVER * (1 - down) + press };
    } else if (t < TIME.set[0]) {
      at = { x: lerp(CUT.x, PLACE.x, carry), y: lerp(CUT.y, PLACE.y, carry) - HOVER * up };
    } else {
      at = { x: PLACE.x, y: PLACE.y - HOVER * (1 - set) - leave * 40 };
    }
    const holding = t >= TIME.up[0] && t < TIME.set[1];
    const cut = t >= TIME.press[0] + 0.04;
    const placed = t >= TIME.set[1];
    const visibleCutter = appear * (1 - leave);

    const sheetTop = polyline(
      [
        { x: SHEET.left + SHEET.slant, y: SHEET.top },
        { x: SHEET.right - SHEET.slant, y: SHEET.top },
        { x: SHEET.right, y: SHEET.bottom },
        { x: SHEET.left, y: SHEET.bottom },
      ],
      true,
    );
    return compact([
      ...counter(),
      // Teigplatte mit Kante
      rect(SHEET.left, SHEET.bottom, SHEET.right - SHEET.left, SHEET_THICKNESS, 1, styles.solid('shade')),
      shape(sheetTop, styles.solid('paper')),
      // Bereits ausgestochene Löcher als Spur früherer Plätzchen.
      shape(outline(form, 40, 108), { fill: 'tint', stroke: 'deep', strokeWidth: STROKE.detail }),
      shape(outline(form, 134, 122), { fill: 'tint', stroke: 'deep', strokeWidth: STROKE.detail }),
      cut
        ? faded(1 - reset, [
            shape(outline(form, CUT.x, CUT.y), { fill: 'tint', stroke: 'deep', strokeWidth: STROKE.detail }),
          ])
        : null,
      // Blech
      rect(TRAY.left, TRAY.y, TRAY.right - TRAY.left, 5, 2, styles.solid('ink')),
      placed ? faded(1 - reset, cookie(form, PLACE)) : null,
      holding ? group(cookie(form, at)) : null,
      faded(visibleCutter, cutter(form, at)),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: CUT.x + 36, y: CUT.y - 50 },
        { x: CUT.x + 30, y: CUT.y - 24 },
      ]),
      arrow([
        { x: CUT.x + 30, y: CUT.y - 60 },
        { x: (CUT.x + PLACE.x) / 2, y: CUT.y - 74 },
        { x: PLACE.x - 4, y: PLACE.y - 34 },
      ]),
    ];
  },
});
